import { providerLabel } from './labels'

// The email panel's timeline in words (owner 2026-10-07): each delivery
// event as a sentence a person can act on, from the readable fields
// rapid-api-content derives (domain email-activity reading.go). A code is
// never shown without its explanation beside it.

// Enhanced status codes (RFC 3463), by subject and detail; the class (4 =
// temporary, 5 = permanent) does not change what they mean.
const STATUS_MEANING = {
  '1.1': "the mailbox doesn't exist",
  '1.10': "the mailbox doesn't exist",
  '1.2': "the email domain doesn't exist or can't receive email",
  '1.3': "the address isn't valid",
  '1.6': 'the mailbox has moved',
  '2.1': 'the mailbox is disabled',
  '2.2': 'the mailbox is full',
  '2.3': 'the email is too large for this mailbox',
  '3.0': 'the receiving mail system has a problem',
  '3.1': 'the receiving mail system is out of space',
  '3.4': 'the email is too large for the receiving server',
  '4.1': "the receiving server didn't answer",
  '4.2': 'the connection to the receiving server dropped',
  '4.4': "the email domain's mail server can't be reached",
  '4.7': "it couldn't be delivered in time and expired",
  '7.0': 'the receiving server refused it because of a security or spam policy',
  '7.1': 'the receiving server refused it because of a security or spam policy',
  '7.25': "the receiving server couldn't confirm who sent it",
  '7.26': "it failed the receiving server's check of who sent it",
  '7.27': "it failed the receiving server's check of who sent it",
  '7.28': 'the receiving server is limiting email from unknown senders'
}

// Temporary (4.x.x) answers that mean something more specific than their
// permanent twin.
const TEMPORARY_MEANING = {
  '7.0': 'the receiving server is limiting how much email it accepts',
  '7.1': 'the receiving server is limiting how much email it accepts',
  '7.28': 'the receiving server is limiting how much email it accepts'
}

// Basic SMTP reply codes, when there is no enhanced code.
const SMTP_MEANING = {
  421: 'the receiving server is busy or limiting connections',
  450: 'the mailbox is temporarily unavailable',
  451: 'the receiving server had a temporary problem',
  452: 'the receiving server is out of space for now',
  550: 'the mailbox is unavailable or refused it',
  551: "the address isn't handled by that server",
  552: 'the mailbox is full or the email is too large',
  553: "the address isn't allowed",
  554: 'the receiving server refused it'
}

// SendGrid's bounce classification.
const CLASSIFICATION_MEANING = {
  'invalid address': "the address doesn't exist",
  'mailbox unavailable': 'the mailbox is unavailable',
  technical: 'a technical problem at the receiving server',
  content: 'the receiving server refused its content',
  reputation: 'the receiving server refused email from our sending service',
  'frequency or volume too high': 'the receiving server is limiting how much email it accepts'
}

// SendGrid's drop reasons.
const DROP_MEANING = {
  'bounced address': 'this address bounced before, so it was not tried again',
  'unsubscribed address': 'this person unsubscribed',
  'spam reporting address': 'this person marked earlier email as spam',
  invalid: "the address isn't valid",
  'invalid address': "the address isn't valid",
  'recipient list over package quota': 'the sending limit was reached'
}

// "the mailbox doesn't exist", from the enhanced status code, else the
// reply code; '' when neither is known.
export const smtpMeaning = ({ statusCode, smtpCode } = {}) => {
  const m = /^([245])\.(\d{1,3}\.\d{1,3})$/.exec(statusCode || '')
  if (m) {
    const detail = m[2]
    if (m[1] === '4' && TEMPORARY_MEANING[detail]) return TEMPORARY_MEANING[detail]
    if (STATUS_MEANING[detail]) return STATUS_MEANING[detail]
  }
  return SMTP_MEANING[smtpCode] || ''
}

// "550 5.1.1": the codes the explanation stands beside.
export const codeText = ({ smtpCode, statusCode } = {}) => [smtpCode, statusCode].filter(Boolean).join(' ')

const withCode = (text, e) => (codeText(e) ? `${text} (${codeText(e)})` : text)

// What the platform's mailed links do, by their path template (content
// keeps only the scheme, host and route words of a clicked link; every
// other segment is :param).
const LINK_LABELS = [
  [/^\/public\/invite(\/|$)/, 'Accept invitation'],
  [/^\/public\/password-recover\/reset(\/|$)/, 'Reset password'],
  [/^\/register-user(\/|$)/, 'Confirm email address'],
  [/^\/open\/sso-consent(\/|$)/, 'Review the single sign-on request'],
  [/^\/org\/sso$/, 'Single sign-on settings'],
  [/^\/org\/organization\/scheduled$/, 'Restore the organization'],
  [/^\/admin\/provider\/list\/edit\//, 'The sign-in connection'],
  [/^\/admin\/provider\/health$/, 'Sign-in connection health'],
  [/^\/admin\/provider\/domains$/, 'Domain verification'],
  [/^\/admin\/provider\/connect$/, 'Connect single sign-on'],
  [/^\/admin\/alert-routing$/, 'Alert routing'],
  [/^\/admin\/organization\/list\/recycle$/, 'Deleted organizations'],
  [/^\/admin\/organization\/list\/edit\//, 'The organization'],
  [/^\/admin\/login\/list\/edit\//, "The person's account"],
  [/^\/admin\/errors(\/|$)/, 'The error']
]

// A clicked link as people say it: { label: 'Accept invitation', known: true }
// for a page the platform mails, else its host and path without the parts
// content left out ({ label: 'educationxr.com/pricing', known: false }).
export const linkText = url => {
  let u
  try {
    u = new URL(url)
  } catch (err) {
    return null
  }
  const path = u.pathname.replace(/\/+$/, '') || '/'
  const hit = LINK_LABELS.find(([re]) => re.test(path))
  if (hit) return { label: hit[1], known: true }
  const host = u.hostname.replace(/^www\./, '')
  const words = path.split('/').filter(s => s && s !== ':param')
  return { label: [host, ...words].join('/'), known: false }
}

// "on iPhone Mail" (the owner's words), "in Gmail", "in Chrome on Windows".
export const clientPhrase = client => (/^(iPhone|iPad)\b/.test(client) ? `on ${client}` : `in ${client}`)

// "France", from an ISO country code; the code itself when the browser
// can't name it.
export const countryName = code => {
  if (!code) return ''
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code
  } catch (err) {
    return code
  }
}

// "Denver, Colorado, United States": an approximate place.
export const placeText = place => {
  if (!place) return ''
  const parts = [place.city, place.region, countryName(place.country)].filter(Boolean)
  return parts.filter((p, i) => parts.indexOf(p) === i).join(', ')
}

const bounceReason = e =>
  smtpMeaning(e) || CLASSIFICATION_MEANING[(e.classification || '').toLowerCase()] || 'the receiving server refused it'

// One event as { title, detail, said, tone }: the sentence, a second line
// that explains it, the receiving server's own words (labelled as theirs by
// the panel), and the dot's colour.
export const eventSentence = e => {
  const said = (e.reason || '').trim()
  switch (e.type) {
    case 'sent':
      return { title: `Accepted by ${providerLabel(e.provider) || 'the email service'} for sending`, tone: 'info' }
    case 'delivered':
      return { title: "Delivered: the recipient's email server accepted it", tone: 'ok' }
    case 'deferred': {
      const lead = e.attempt ? `Deferred, attempt ${e.attempt}` : 'Deferred'
      return {
        title: withCode(`${lead}: ${smtpMeaning(e) || 'the receiving server asked to try again later'}`, e),
        detail: 'The email service keeps trying for up to three days.',
        said,
        tone: 'warn'
      }
    }
    case 'bounced':
      return { title: withCode(`${e.blocked ? 'Blocked' : 'Bounced'}: ${bounceReason(e)}`, e), said, tone: 'bad' }
    case 'dropped': {
      const meaning = DROP_MEANING[said.toLowerCase()]
      return { title: `Not sent: ${meaning || 'the email service stopped it'}`, said: meaning ? '' : said, tone: 'bad' }
    }
    case 'opened':
      if (e.machineOpen) {
        return {
          title: 'Opened automatically, maybe not by a person',
          detail: 'Apple Mail Privacy Protection and some security tools open email to load its pictures, so this open may not mean someone read it.',
          tone: 'info'
        }
      }
      return { title: e.client ? `Opened ${clientPhrase(e.client)}` : 'Opened', tone: 'ok' }
    case 'clicked': {
      const link = e.url ? linkText(e.url) : null
      const title = !link ? 'Clicked a link' : link.known ? `Clicked '${link.label}'` : `Clicked a link to ${link.label}`
      return { title, detail: e.client ? `In ${e.client}` : '', tone: 'ok' }
    }
    case 'spam_report':
      return { title: 'Marked as spam by the recipient', detail: 'Further email to them may not be delivered.', tone: 'bad' }
    case 'unsubscribed':
      return { title: 'Unsubscribed', tone: 'warn' }
    default:
      if (e.providerEvent === 'group_resubscribe') return { title: 'Subscribed again', tone: null }
      return { title: `Another update from ${providerLabel(e.provider) || 'the email service'}`, tone: null }
  }
}
